class ProductConfigurator extends HTMLElement {
  constructor() {
    super();

    // ---------- Helpers ----------
    const first = (root, csv) => {
      if (!csv) return null;
      for (const sel of csv.split(',').map(s => s.trim())) {
        const el = (root || document).querySelector(sel);
        if (el) return el;
      }
      return null;
    };

    // ---------- Flexible Selektoren (Broadcast-kompatibel) ----------
    // Add-to-cart Button (häufige Varianten in Broadcast/OS2.0)
    const addBtnSel = [
      "[data-product-form] [name='add']",
      "button[name='add']",
      ".add-to-cart",
      ".btn--add-to-cart",
      ".product-form__add-button"
    ].join(",");

    // Preisfelder
    const priceSel   = "[data-product-price], .price__current, [data-price], .price .current";
    const compareSel = "[data-compare-price], .price__compare, [data-compare-price-text], .price .compare";

    // Drawer-Trigger (nur EINEN benutzen, in dieser Reihenfolge probieren)
    const cartOpenSel = [
      "[data-drawer-trigger='cart']",
      "[data-open-drawer='cart']",
      "[data-cart-drawer-toggle]",
      ".js-drawer-open-cart",
      ".js-cart-trigger",
      ".header__icon--cart",
      ".cart-link",
      "button[aria-controls='CartDrawer']"
    ].join(",");

    // Drawer-Container & Content (Broadcast/Impulse/Generisch)
    this.drawerSelCandidates = [
      "#CartDrawer",
      "cart-drawer",
      "[data-cart-drawer]",
      "[data-section-id='cart-drawer']",
    ];
    this.drawerInnerSelCandidates = [
      ".drawer__inner",
      ".cart-drawer__content",
      ".minicart__content",
      "[data-cart-drawer-contents]"
    ];

    // ---------- DOM-Referenzen ----------
    this.cartDrawerOpenBtn = first(document, cartOpenSel);
    this.addButton = first(document, addBtnSel);

    this.price = first(this, priceSel) || first(document, priceSel);
    this.comparePrices = document.querySelectorAll(compareSel);

    this.getOptions = this.querySelectorAll('.product-configurator__option');
    this.getDiscountOptions = this.querySelector('.product-configurator__options-discount');
    this.removeDiscountButtons = this.querySelectorAll('.product-configurator__remove-discount');
    this.getVariants = this.querySelectorAll('.product-configurator__variant');
    this.variantWrapper = this.querySelectorAll('.product-configurator__variants-wrap');
    this.counter = this.querySelector('.product-configurator__counter');

    const unpickedImageEl = this.querySelector('.product-configurator__option-img');
    this.unpickedImage = unpickedImageEl ? unpickedImageEl.src : '';

    this.titles = this.querySelectorAll('.product-configurator__title');
    this.optionTitles = this.querySelectorAll('.product-configurator__option-title');

    const variantsDataElement = document.querySelector('#variantsDataJson');
    this.variantsDataJson = variantsDataElement ? JSON.parse(variantsDataElement.textContent) : [];

    // ---------- State ----------
    this.optionIndex = 0;
    this.optionQuantity = this.getOptions.length;
    this.discountQuantity = this.getDiscountOptions ? this.getDiscountOptions.children.length : 0;
    this.variantTag = [0, 0];
    this.pickedId = null;

    // ---------- Init ----------
    this.disabledButtons();
    this.setOption();
    this.removeOption();
    this.setupAddButtonListener();
    this.setupOutsideClickListener();
  }

  // ---------- Listeners / UX ----------
  setupAddButtonListener() {
    if (!this.addButton) return;
    if (this._boundHandleAddClick) {
      this.addButton.removeEventListener('click', this._boundHandleAddClick, true);
    }
    this._boundHandleAddClick = this.handleAddClick.bind(this);
    this.addButton.addEventListener('click', this._boundHandleAddClick, true);
  }

  async handleAddClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.addButton.getAttribute('aria-disabled') === 'false') {
      await this.sendProductVariants();
    } else if (this.variantWrapper[0]) {
      this.variantWrapper[0].setAttribute('data-show', 'true');
      this.setAttribute('data-show', 'true');
    }
  }

  setupOutsideClickListener() {
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) &&
          this.addButton?.getAttribute('aria-disabled') === 'false') {
        this.hideAllVariants();
      }
    });
  }

  hideAllVariants() {
    this.variantWrapper.forEach(w => w.setAttribute('data-show', 'false'));
    this.setAttribute('data-show', 'false');
  }

  // ---------- Optionen / Varianten ----------
  setOption() {
    this.getOptions.forEach((option, index) => {
      option.addEventListener('click', () => {
        this.changeVariant(option, index);
        this.removeVariant(option, index);
        this.getOptionQuantity();
        this.getPickedVariant();
        this.setActiveProductsBundle(option, index);
        this.setBorder();
      });
    });
    this.pickVariant();
  }

  removeOption() {
    this.removeDiscountButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = +btn.parentElement.dataset.index;
        if (idx === this.optionQuantity - 1) {
          this.removeVariant(btn.parentElement, idx);
          this.getOptionQuantity();
          this.getPickedVariant();
          this.setActiveProductsBundle();
          this.getOptions.forEach(o => o.setAttribute('data-border', 'false'));
          const emptyIdx = this.checkEmptyOption();
          if (this.getOptions[emptyIdx]) this.getOptions[emptyIdx].setAttribute('data-border', 'true');
        } else {
          btn.parentElement.setAttribute('data-discount', 'false');
          this.removeVariant(btn.parentElement, idx);
          this.getOptionQuantity();
          this.getPickedVariant();
          this.setActiveProductsBundle();
        }

        if (+this.counter.dataset.quantity === this.optionQuantity - this.discountQuantity) {
          if (this.getDiscountOptions) {
            [...this.getDiscountOptions.children].forEach(o => o.setAttribute('data-discount', 'false'));
            const emptyIdx = this.checkEmptyOption();
            if (this.getDiscountOptions.children[0]) this.getDiscountOptions.children[0].setAttribute('data-discount', 'true');
            this.getOptions.forEach(o => o.setAttribute('data-border', 'false'));
            if (this.getOptions[emptyIdx]) this.getOptions[emptyIdx].setAttribute('data-border', 'true');
          }
        }

        this.hideAllVariants();
      });
    });
  }

  pickVariant() {
    this.getVariants.forEach(variant => {
      variant.addEventListener('click', () => {
        if (variant.getAttribute('data-disabled') === 'false' &&
            +this.counter.dataset.quantity < this.optionQuantity) {
          this.optionIndex = this.checkEmptyOption();
          const option = this.getOptions[this.optionIndex];
          const img = variant.firstElementChild;
          if (option && img) option.firstElementChild.src = img.src;

          const title = variant.getAttribute('data-title');
          const sku = variant.getAttribute('data-sku');
          const expensive = variant.getAttribute('data-expensive');

          if (option) {
            option.setAttribute('data-title', title);
            option.setAttribute('data-sku', sku);
            option.setAttribute('data-expensive', expensive);
            option.setAttribute('data-checked', 'true');
          }

          this.getOptionQuantity();
          this.optionIndex++;
          this.setIndexRange();
          this.setBorder();
          this.getPickedVariant();
          this.setActiveProductsBundle();
        }
        this.setCheckMark(variant);
      });
    });
  }

  checkEmptyOption() {
    let emptyIndex = 0;
    [...this.getOptions].some((option, index) => {
      if (option.getAttribute('data-checked') === 'false') { emptyIndex = index; return true; }
      return false;
    });
    return emptyIndex;
  }

  setIndexRange() {
    if (this.optionIndex >= this.optionQuantity) this.optionIndex = this.optionQuantity - 1;
    if (this.optionIndex < 0) this.optionIndex = 0;
  }

  changeVariant(option, index) {
    const image = option.querySelector('img');
    if (!image) return;
    if (option.getAttribute('data-checked') === 'true' &&
        +this.counter.dataset.quantity === (this.optionQuantity - this.discountQuantity)) {
      this.optionIndex = index;
      this.counter.dataset.quantity = this.optionQuantity - 1;
      image.src = this.unpickedImage || image.src;
      option.setAttribute('data-checked', 'false');
      option.removeAttribute('data-title');
      option.removeAttribute('data-sku');
      this.disabledButtons();
      this.getPickedVariant();
    }
  }

  removeVariant(option, index) {
    const image = option.querySelector('img');
    if (!image) return;
    if (+this.counter.dataset.quantity <= this.optionQuantity &&
        option.getAttribute('data-checked') === 'true') {
      this.optionIndex = index;
      this.counter.dataset.quantity = Math.max(0, +this.counter.dataset.quantity - 1);
      image.src = this.unpickedImage || image.src;
      option.setAttribute('data-checked', 'false');
      option.removeAttribute('data-title');
      option.removeAttribute('data-sku');
      this.getPickedVariant();
    }
  }

  setBorder() {
    const empty = this.getOptions[this.checkEmptyOption()];
    if (empty?.getAttribute('data-checked') === "false") {
      this.getOptions.forEach(o => o.setAttribute('data-border', 'false'));
      empty.setAttribute('data-border', 'true');
      empty.setAttribute('data-discount', 'true');
    }
  }

  getOptionQuantity() {
    let quantity = 0;
    this.getOptions.forEach(o => { if (o.getAttribute('data-checked') === 'true') quantity++; });
    this.counter.dataset.quantity = quantity;

    if (quantity === (this.optionQuantity - this.discountQuantity)) this.enabledButtons();

    if (quantity === this.optionQuantity) {
      this.getOptions.forEach(o => o.setAttribute('data-border', 'false'));
      this.hideAllVariants();
    }
  }

  // ---------- Add to Cart + Drawer Refresh ----------
  async sendProductVariants() {
    const variants = [];
    const skues = [];

    this.getOptions.forEach(option => {
      const { title, sku } = option.dataset;
      if (title) { variants.push(title); skues.push(sku); }
    });

    await this.addVariants(variants, skues);
  }

  async addVariants(variants, skues) {
    if (!this.pickedId) {
      alert('Bitte wähle zuerst eine Variante aus.');
      return;
    }

    const payload = {
      items: [{
        id: this.pickedId,
        quantity: 1,
        properties: {
          "Varianten": variants.join(', '),
          "_Artikelnummern": skues.join(', ')
        }
      }]
    };

    try {
      this.addButton?.setAttribute('data-loading', 'true');
      this.addButton?.setAttribute('disabled', 'true');

      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errTxt = await res.text();
        alert('Konnte den Artikel nicht in den Warenkorb legen.\n' + errTxt);
        return;
      }

      await res.json(); // nicht zwingend benötigt, aber löst evtl. Theme-Hooks aus

      // Nur Drawer/Items aktualisieren (Header bleibt unberührt)
      await this.refreshAfterAdd();

      // Drawer NATIV öffnen (wie im Theme)
      this.openCartDrawer();

    } catch (e) {
      console.error('[ProductConfigurator] ATC Fehler', e);
      alert('Konnte den Artikel nicht in den Warenkorb legen. Bitte Seite aktualisieren.');
    } finally {
      this.addButton?.removeAttribute('data-loading');
      this.addButton?.removeAttribute('disabled');
    }
  }

  /** Aktualisiert Cart-Drawer-Inhalt; Header wird NICHT ersetzt. */
  async refreshAfterAdd() {
    // 1) Versuche echte Sections (Broadcast hat meist eine Cart-Drawer-Section)
    const knownSections = ['cart-drawer','cart-drawer-items','cart']; // 'cart' optional falls /cart offen ist
    try {
      const url = `${window.Shopify.routes.root}?sections=${encodeURIComponent(knownSections.join(','))}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const htmlMap = await res.json();

      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      const getDrawerEl = () => {
        for (const sel of this.drawerSelCandidates) {
          const el = document.querySelector(sel);
          if (el) return el;
        }
        return null;
      };
      const getInner = (root) => {
        if (!root) return null;
        for (const sel of this.drawerInnerSelCandidates) {
          const el = root.querySelector(sel);
          if (el) return el;
        }
        return null;
      };

      const currentDrawer = getDrawerEl();
      const currentInner = getInner(currentDrawer);
      if (!currentInner) throw new Error('Drawer DOM nicht gefunden');

      // Quelle: bevorzugt 'cart-drawer' HTML-Schlüssel
      const sourceKeys = ['cart-drawer','cart-drawer-items','cart'];
      let replaced = false;

      for (const key of sourceKeys) {
        if (!htmlMap[key]) continue;
        const newDoc = parse(htmlMap[key]);
        // passende neue Drawer-Inner suchen
        let newInner = null;
        // zuerst im neuen Markup direkt
        for (const sel of this.drawerInnerSelCandidates) {
          const cand = newDoc.querySelector(sel);
          if (cand) { newInner = cand; break; }
        }
        // oder im Drawer-Container des neuen Markups
        if (!newInner) {
          for (const sel of this.drawerSelCandidates) {
            const cont = newDoc.querySelector(sel);
            if (cont) {
              newInner = getInner(cont);
              if (newInner) break;
            }
          }
        }
        if (newInner) {
          currentInner.innerHTML = newInner.innerHTML;
          replaced = true;
          break;
        }
      }

      if (!replaced) throw new Error('Kein Drawer-Inhalt in Sections-Antwort');

      await this.refreshCartCountFallback();

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      return; // erfolgreich

    } catch (err) {
      // 2) Fallback: Header-Section holen (ohne Header im DOM zu ersetzen) und daraus nur den Drawer-Inhalt kopieren
      console.warn('[Configurator] Sections-Refresh fehlgeschlagen, Fallback auf header-section', err);
      try {
        const hdr = await fetch(`${window.Shopify.routes.root}?section_id=header`);
        const text = await hdr.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const getInner = (root) => {
          if (!root) return null;
          for (const sel of this.drawerInnerSelCandidates) {
            const el = root.querySelector(sel);
            if (el) return el;
          }
          return null;
        };

        let newInner = null;
        // im geladenen Header nach CartDrawer suchen
        const possibleContainers = ['#CartDrawer','cart-drawer','[data-cart-drawer]'];
        let newContainer = null;
        for (const sel of possibleContainers) {
          const c = doc.querySelector(sel);
          if (c) { newContainer = c; break; }
        }
        if (newContainer) newInner = getInner(newContainer);

        const currentDrawer = document.querySelector('#CartDrawer, cart-drawer, [data-cart-drawer]');
        const currentInner = getInner(currentDrawer);

        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

        await this.refreshCartCountFallback();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      } catch (fallbackErr) {
        console.warn('[Configurator] Header-Fallback fehlgeschlagen', fallbackErr);
        await this.refreshCartCountFallback(); // wenigstens Badge aktualisieren
      }
    }
  }

  async refreshCartCountFallback() {
    try {
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      const count = cart.item_count;
      document.querySelectorAll(
        '[data-cart-count], .cart-link__bubble, .header__cart-count, .cart-count-bubble__count, .header-cart__count'
      ).forEach(el => {
        el.textContent = count;
        el.setAttribute?.('data-cart-count', count);
      });
      // sichtbarkeits-klassik (falls Themes Bubbles ein-/ausblenden)
      document.querySelectorAll('.cart-link__bubble, .cart-count-bubble, .header-cart__bubble')
        .forEach(b => {
          if (count > 0) b.classList.add('cart-link__bubble--visible','is-visible');
          else b.classList.remove('cart-link__bubble--visible','is-visible');
        });
    } catch {/* stilles Fallback */}
  }

  openCartDrawer() {
    const trigger = this.cartDrawerOpenBtn || document.querySelector(
      "[data-drawer-trigger='cart'], [data-open-drawer='cart'], [data-cart-drawer-toggle], .js-drawer-open-cart, .js-cart-trigger, .header__icon--cart, .cart-link, button[aria-controls='CartDrawer']"
    );
    if (trigger) { trigger.click(); return; }
    // Theme-Events als Fallback
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
  }

  // ---------- Variantenauswahl / Preisdaten ----------
  getPickedVariant() {
    let quantity = 0;
    this.getOptions.forEach(option => {
      if (option.hasAttribute('data-option') && option.dataset.checked === 'true') quantity++;
    });

    const current = +this.counter.dataset.quantity || quantity;
    const threshold = this.optionQuantity - this.discountQuantity;

    this.counter.textContent = current >= threshold ? current : quantity;
    if (this.counter.nextElementSibling) {
      const sizeLabel = this.dataset.size || this.optionQuantity;
      const sel = current >= threshold ? current : quantity;
      this.counter.nextElementSibling.textContent = `von ${sizeLabel} ausgewählt`;
    }

    this.setDataFromJSON();
  }

  setDataFromJSON() {
    let expensiveCount = 0;
    this.getOptions.forEach(option => {
      const { title, expensive } = option.dataset;
      if (title && expensive === 'true') expensiveCount++;
      this.updateVariantTag();
    });

    this.variantTag[1] = expensiveCount;
    const variantKey = this.variantTag.join('');
    const selected = this.findSelectedVariant(variantKey);
    if (selected) this.updateData(selected);
  }

  updateVariantTag() {
    const current = +this.counter.dataset.quantity || 0;
    const threshold = this.optionQuantity - this.discountQuantity;
    this.variantTag[0] = current >= threshold ? current : threshold;
  }

  findSelectedVariant(variantKey) {
    return this.variantsDataJson.find(variant => {
      const stripped = (variant.title || '').replace(/\D/g, '');
      if (stripped.length > 1) return stripped === variantKey;
      return stripped === variantKey[0];
    });
  }

  updateData(v) {
    this.pickedId = Number(v.id);
    if (this.price) this.price.textContent = this.formatMoney(v.price);
    if (this.comparePrices?.length && v.compare_at_price) {
      this.comparePrices.forEach(el => el.textContent = this.formatMoney(v.compare_at_price));
    }
  }

  formatMoney(price) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Fallback auf Shopify.formatMoney, falls vorhanden
      try { return Shopify.formatMoney(price, "€{{amount_no_decimals}}"); } catch {}
    }
    return "€" + (price / 100).toFixed(2).replace(".", ",").replace(",00", "");
  }

  // ---------- Buttons ----------
  disabledButtons() {
    if (this.addButton) {
      this.addButton.setAttribute('aria-disabled', 'true');
      this.addButton.parentElement?.setAttribute('aria-disabled', 'true');
    }
  }

  enabledButtons() {
    if (this.addButton) {
      this.addButton.setAttribute('aria-disabled', 'false');
      this.addButton.parentElement?.setAttribute('aria-disabled', 'false');
    }
  }

  // ---------- Anzeige-Logik ----------
  setActiveProductsBundle(option, index) {
    const currentIndex = option ? +option.dataset.index : 0;
    const currentQuantity = +this.counter.dataset.quantity || 0;
    const threshold = this.optionQuantity - this.discountQuantity;

    if (this.variantWrapper.length > 1) {
      if (currentIndex < 2) {
        this.showVariantWrapper(0); this.showTitle(0);
      } else {
        if (currentQuantity > 1) { this.showVariantWrapper(1); this.showTitle(1); }
        else { this.showVariantWrapper(0); this.showTitle(0); }
      }
    } else {
      if (currentQuantity >= threshold) {
        if (currentQuantity === this.optionQuantity) this.hideAllVariants();
        else this.variantWrapper[0]?.setAttribute('data-show', 'true');
      } else {
        this.variantWrapper[0]?.setAttribute('data-show', 'true');
      }
      this.setAttribute('data-show', 'true');
    }
  }

  showVariantWrapper(i) {
    this.variantWrapper.forEach((w, idx) => w.setAttribute('data-show', idx === i ? 'true' : 'false'));
    this.setAttribute('data-show', 'true');
  }

  showTitle(i) {
    this.titles.forEach((t, idx) => t.setAttribute('data-show', idx === i ? 'true' : 'false'));
  }

  setCheckMark(variant) {
    const wrapper = variant.closest('.product-configurator__variants-wrap');
    const markTimeout = parseInt(variant.dataset.time || '1') * 1000;
    this.getVariants.forEach(v => v.removeAttribute('data-mark'));
    if (wrapper?.dataset.show !== "false") {
      variant.setAttribute('data-mark', '');
      setTimeout(() => variant.removeAttribute('data-mark'), markTimeout);
    }
  }
}

customElements.define('product-configurator', ProductConfigurator);
